import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TIME_ZONE_OPTIONS } from '../utils/timeZone';
import { 
  ArrowLeft, 
  Loader2, 
  Save, 
  Shield, 
  AlertCircle, 
  User, 
  Phone, 
  MapPin,
  Heart,
  DollarSign,
  Accessibility,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// US States
const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

// SSN formatting helper
const formatSSN = (value) => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 9)}`;
};

// Validate SSN format
const validateSSN = (ssn) => {
  const cleaned = ssn.replace(/-/g, '');
  if (cleaned.length !== 9) return false;
  if (!/^\d{9}$/.test(cleaned)) return false;
  if (cleaned.slice(0, 3) === '000') return false;
  if (cleaned.slice(3, 5) === '00') return false;
  if (cleaned.slice(5) === '0000') return false;
  if (cleaned.startsWith('9')) return false;
  return true;
};

// Format phone number
const formatPhone = (value) => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const ParticipantFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ssnError, setSSNError] = useState('');
  const [intakeOptions, setIntakeOptions] = useState({ disabilities: [], barriers_to_employment: [] });

  const [formData, setFormData] = useState({
    // SSN (Case Number)
    ssn: '',
    ssn_last_four: '',
    // State ID (optional)
    state_id: '',
    // Personal Info
    first_name: '',
    last_name: '',
    alias_name: '',
    date_of_birth: '',
    // Demographics
    gender: '',
    race: '',
    primary_language: 'English',
    // Homeless status
    is_homeless: false,
    // Basic Contact
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    // Emergency Contact
    emergency_contact: {
      name: '',
      relationship: '',
      phone: ''
    },
    // Income Information
    income_info: {
      monthly_income: 0,
      household_size: 1,
      is_unemployed: false,
      receiving_snap: false
    },
    // Multi-selects
    disabilities: [],
    barriers_to_employment: [],
    demographics: {}
  });

  useEffect(() => {
    fetchIntakeOptions();
    if (isEdit) {
      fetchParticipant();
    }
  }, [id]);

  const fetchIntakeOptions = async () => {
    try {
      const response = await axios.get(`${API}/intake-options/all`);
      setIntakeOptions(response.data);
    } catch (error) {
      console.error('Failed to load intake options:', error);
    }
  };

  const fetchParticipant = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/participants/${id}`);
      const p = response.data;
      setFormData({
        ssn: p.ssn || p.ssn_masked || '',
        ssn_last_four: p.ssn_last_four || '',
        state_id: p.state_id || '',
        first_name: p.first_name || '',
        last_name: p.last_name || '',
        alias_name: p.alias_name || '',
        date_of_birth: p.date_of_birth || '',
        gender: p.gender || '',
        race: p.race || '',
        primary_language: p.primary_language || 'English',
        is_homeless: p.is_homeless || false,
        email: p.email || '',
        phone: p.phone || '',
        address_line1: p.address_line1 || p.address || '',
        address_line2: p.address_line2 || '',
        city: p.city || '',
        state: p.state || '',
        zip_code: p.zip_code || '',
        time_zone: p.time_zone || '',
        emergency_contact: p.emergency_contact || { name: '', relationship: '', phone: '' },
        income_info: p.income_info || { monthly_income: 0, household_size: 1, is_unemployed: false, receiving_snap: false },
        disabilities: p.disabilities || [],
        barriers_to_employment: p.barriers_to_employment || [],
        demographics: p.demographics || {}
      });
    } catch (error) {
      toast.error('Failed to load participant');
      navigate('/participants');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSSNChange = (e) => {
    const formatted = formatSSN(e.target.value);
    setFormData(prev => ({ ...prev, ssn: formatted }));
    if (ssnError) setSSNError('');
  };

  const handleSSNBlur = () => {
    if (formData.ssn && !validateSSN(formData.ssn)) {
      setSSNError('Invalid SSN format. Must be 9 digits (XXX-XX-XXXX)');
    } else {
      setSSNError('');
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, phone: formatted }));
  };

  const handleEmergencyContactChange = (field, value) => {
    if (field === 'phone') {
      value = formatPhone(value);
    }
    setFormData(prev => ({
      ...prev,
      emergency_contact: { ...prev.emergency_contact, [field]: value }
    }));
  };

  const handleIncomeChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      income_info: { ...prev.income_info, [field]: value }
    }));
  };

  const handleMultiSelectToggle = (field, optionId) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(optionId)
        ? prev[field].filter(id => id !== optionId)
        : [...prev[field], optionId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isEdit && !validateSSN(formData.ssn)) {
      setSSNError('Valid SSN is required for new participants');
      return;
    }
    
    setSaving(true);

    try {
      if (isEdit) {
        const { ssn, ...updateData } = formData;
        await axios.put(`${API}/participants/${id}`, updateData);
        toast.success('Participant updated successfully');
        navigate(`/participants/${id}`);
      } else {
        const response = await axios.post(`${API}/participants`, formData);
        toast.success('Participant created successfully');
        navigate(`/participants/${response.data.id}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && detail.includes('already exists')) {
        setSSNError(detail);
      } else {
        toast.error(detail || 'Failed to save participant');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="participant-form-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? 'Edit Participant' : 'New Participant Intake'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isEdit ? 'Update participant profile and intake information' : 'Complete intake form for new participant'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Case Number (SSN) */}
        {!isEdit && (
          <Card className="card-shadow border-primary/20">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <CardTitle>Case Number (Social Security Number)</CardTitle>
              </div>
              <CardDescription>
                SSN is required at intake and becomes the unique Participant ID. Must be exactly 9 digits.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="ssn">Social Security Number (SSN) *</Label>
                <Input
                  id="ssn"
                  name="ssn"
                  value={formData.ssn}
                  onChange={handleSSNChange}
                  onBlur={handleSSNBlur}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                  required
                  className={ssnError ? 'border-red-500' : ''}
                  data-testid="ssn-input"
                />
                {ssnError && (
                  <Alert variant="destructive" className="mt-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{ssnError}</AlertDescription>
                  </Alert>
                )}
                <p className="text-xs text-muted-foreground">
                  SSN will be masked by default (***-**-1234). Full SSN visible only to authorized internal users.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Display masked SSN for edit mode */}
        {isEdit && (formData.ssn || formData.ssn_last_four) && (
          <Card className="card-shadow bg-muted/30">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Case Number (SSN)</p>
                  <p className="text-lg font-mono">
                    {formData.ssn_last_four 
                      ? `***-**-${formData.ssn_last_four}` 
                      : formData.ssn}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                The SSN/Participant ID cannot be changed after creation.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Section 2: Personal Information */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              <CardTitle>Personal Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                required
                data-testid="first-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                required
                data-testid="last-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="alias_name">Alias Name (Optional)</Label>
              <Input
                id="alias_name"
                name="alias_name"
                value={formData.alias_name}
                onChange={handleChange}
                placeholder="Also known as / Goes by"
                data-testid="alias-name-input"
              />
              <p className="text-xs text-muted-foreground">
                Alternative name the participant goes by (used for check-in matching)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Date of Birth *</Label>
              <Input
                id="date_of_birth"
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth}
                onChange={handleChange}
                required
                data-testid="dob-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section: State ID (Optional) */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              <CardTitle>State Identification</CardTitle>
            </div>
            <CardDescription>
              Optional state-issued ID number for additional identification
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-w-md">
              <Label htmlFor="state_id">State ID Number</Label>
              <Input
                id="state_id"
                name="state_id"
                value={formData.state_id}
                onChange={handleChange}
                placeholder="e.g., 1234567"
                data-testid="state-id-input"
              />
              <p className="text-xs text-muted-foreground">
                State-Issued Identifier (optional)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Section: Demographics */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              <CardTitle>Demographics</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="gender">Gender</Label>
                <Select
                  value={formData.gender || "none"}
                  onValueChange={(value) => setFormData({...formData, gender: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select gender</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Non-binary">Non-binary</SelectItem>
                    <SelectItem value="Prefer not to answer">Prefer not to answer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="race">Race</Label>
                <Select
                  value={formData.race || "none"}
                  onValueChange={(value) => setFormData({...formData, race: value === "none" ? "" : value})}
                >
                  <SelectTrigger data-testid="race-select">
                    <SelectValue placeholder="Select race" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select race</SelectItem>
                    <SelectItem value="White">White</SelectItem>
                    <SelectItem value="Hispanic or Latino">Hispanic or Latino</SelectItem>
                    <SelectItem value="Black">Black</SelectItem>
                    <SelectItem value="Asian">Asian</SelectItem>
                    <SelectItem value="American Indian or Alaska Native">American Indian or Alaska Native</SelectItem>
                    <SelectItem value="Middle Eastern or North African">Middle Eastern or North African</SelectItem>
                    <SelectItem value="Native Hawaiian or Other Pacific Islander">Native Hawaiian or Other Pacific Islander</SelectItem>
                    <SelectItem value="Don&apos;t Know">Don&apos;t Know</SelectItem>
                    <SelectItem value="Prefer not to answer">Prefer not to answer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="primary_language">Primary Language</Label>
                <Select
                  value={formData.primary_language || "English"}
                  onValueChange={(value) => setFormData({...formData, primary_language: value})}
                >
                  <SelectTrigger data-testid="primary-language-select">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="English">English</SelectItem>
                    <SelectItem value="Spanish">Spanish</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Basic Contact Information */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-primary" />
              <CardTitle>Contact Information</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone">Telephone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  placeholder="(555) 123-4567"
                  data-testid="phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="participant@email.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <Label className="text-base font-medium">Address</Label>
              </div>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address_line1">Address Line 1</Label>
                  <Input
                    id="address_line1"
                    name="address_line1"
                    value={formData.address_line1}
                    onChange={handleChange}
                    placeholder="123 Main Street"
                    data-testid="address1-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
                  <Input
                    id="address_line2"
                    name="address_line2"
                    value={formData.address_line2}
                    onChange={handleChange}
                    placeholder="Apt 4B"
                    data-testid="address2-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    data-testid="city-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Select value={formData.state} onValueChange={(val) => setFormData(prev => ({ ...prev, state: val }))}>
                    <SelectTrigger data-testid="state-input">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="zip_code">Postal Code</Label>
                  <Input
                    id="zip_code"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleChange}
                    placeholder="12345"
                    maxLength={10}
                    data-testid="zip-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time Zone</Label>
                  <Select value={formData.time_zone || '_auto'} onValueChange={(v) => setFormData(prev => ({ ...prev, time_zone: v === '_auto' ? '' : v }))}>
                    <SelectTrigger data-testid="time-zone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_auto">Set from postal code</SelectItem>
                      {TIME_ZONE_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Homeless Checkbox */}
              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="is_homeless"
                  checked={formData.is_homeless}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_homeless: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  data-testid="homeless-checkbox"
                />
                <Label htmlFor="is_homeless" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  Currently experiencing homelessness
                </Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Emergency Contact */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-red-500" />
              <CardTitle>Emergency Contact</CardTitle>
            </div>
            <CardDescription>Primary emergency contact person</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label>Contact Name</Label>
              <Input
                value={formData.emergency_contact?.name || ''}
                onChange={(e) => handleEmergencyContactChange('name', e.target.value)}
                placeholder="John Doe"
                data-testid="ec-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Relationship</Label>
              <Select 
                value={formData.emergency_contact?.relationship || ''} 
                onValueChange={(val) => handleEmergencyContactChange('relationship', val)}
              >
                <SelectTrigger data-testid="ec-relationship-input">
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="sibling">Sibling</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="friend">Friend</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Telephone Number</Label>
              <Input
                type="tel"
                value={formData.emergency_contact?.phone || ''}
                onChange={(e) => handleEmergencyContactChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                data-testid="ec-phone-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 5: Income Information */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              <CardTitle>Income Information</CardTitle>
            </div>
            <CardDescription>Financial and employment status</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Monthly Income Amount</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.income_info?.monthly_income || 0}
                onChange={(e) => handleIncomeChange('monthly_income', parseFloat(e.target.value) || 0)}
                placeholder="0.00"
                data-testid="monthly-income-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Household Size</Label>
              <Input
                type="number"
                min="1"
                value={formData.income_info?.household_size || 1}
                onChange={(e) => handleIncomeChange('household_size', Math.max(1, parseInt(e.target.value) || 1))}
                data-testid="household-size-input"
              />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <Label className="text-base">Is Participant Unemployed?</Label>
                <p className="text-sm text-muted-foreground">Currently without employment</p>
              </div>
              <Switch
                checked={formData.income_info?.is_unemployed || false}
                onCheckedChange={(checked) => handleIncomeChange('is_unemployed', checked)}
                data-testid="is-unemployed-toggle"
              />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <Label className="text-base">Receiving SNAP Benefits?</Label>
                <p className="text-sm text-muted-foreground">Currently receiving food assistance</p>
              </div>
              <Switch
                checked={formData.income_info?.receiving_snap || false}
                onCheckedChange={(checked) => handleIncomeChange('receiving_snap', checked)}
                data-testid="receiving-snap-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 6: Disabilities (Multi-Select) */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Accessibility className="w-5 h-5 text-blue-600" />
              <CardTitle>Disabilities</CardTitle>
            </div>
            <CardDescription>Select all that apply</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {intakeOptions.disabilities.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleMultiSelectToggle('disabilities', option.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.disabilities.includes(option.id)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`disability-${option.id}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.disabilities.includes(option.id)}
                      onChange={() => {}}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">{option.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Section 7: Barriers to Employment (Multi-Select) */}
        <Card className="card-shadow">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-orange-600" />
              <CardTitle>Barriers to Employment</CardTitle>
            </div>
            <CardDescription>Select all that apply - reportable by program, state, and contract</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              {intakeOptions.barriers_to_employment.map((option) => (
                <div
                  key={option.id}
                  onClick={() => handleMultiSelectToggle('barriers_to_employment', option.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    formData.barriers_to_employment.includes(option.id)
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid={`barrier-${option.id}`}
                >
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.barriers_to_employment.includes(option.id)}
                      onChange={() => {}}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium">{option.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Note about Program Enrollments */}
        {!isEdit && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              After creating the participant, you can enroll them in programs and open cases from their profile page.
            </AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn">
            Cancel
          </Button>
          <Button type="submit" disabled={saving || (!isEdit && ssnError)} data-testid="save-btn">
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? 'Update Participant' : 'Create Participant'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ParticipantFormPage;
